import * as Sentry from "@sentry/nextjs";
import { ClientError } from "graphql-request";
import { shouldSuppressAuthToast } from "@/lib/auth/session-recovery";
import { isSentinelValue } from "@/lib/env";

const sentryUserErrorMessage = (error: unknown): string => {
  if (error instanceof ClientError) {
    return error.response.errors?.[0]?.message ?? `GraphQL Error (Code: ${String(error.response.status)})`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

/**
 * Returns a user-facing error string that is safe to display in the UI.
 *
 * - Auth/session errors: returns `fallback` (redirect / session heal owns the UX;
 *   avoids raw GraphQL "Unauthenticated." toast spam).
 * - In development / staging: returns the raw error message so engineers can
 *   see the full detail inline (e.g. GraphQL JSON payloads).
 * - In production: logs a Sentry-safe error summary, then returns the
 *   provided `fallback` string so users never see raw JSON.
 *
 * graphql-request ClientError.message is `${graphqlError}: ${JSON.stringify({
 *   response, request })}`. `response.body` is the full HTTP text and `request`
 *   is `{ query, variables }`. `beforeSendLog` only scrubs attribute *keys*, so
 *   shipping that string on `{ error }` would send the dump to Sentry Cloud.
 */
export const formatUserError = (error: unknown, fallback: string): string => {
  if (shouldSuppressAuthToast(error)) {
    return fallback;
  }

  // NEXT_PUBLIC_SENTRY_ENVIRONMENT is the only variant inlined into the browser
  // bundle; without it a client-side call would fall back to NODE_ENV and treat
  // stage like production. An unreplaced build-time sentinel is treated as unset.
  const publicEnvironment = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT;
  const environment = (
    (publicEnvironment && !isSentinelValue(publicEnvironment) ? publicEnvironment : undefined) ??
    process.env.SENTRY_ENVIRONMENT ??
    process.env.NODE_ENV ??
    ""
  ).toLowerCase();
  const hideDetails = environment === "main" || environment.startsWith("prod");

  if (hideDetails) {
    Sentry.logger.error("ui.user_error", { error: sentryUserErrorMessage(error) });

    return fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

/**
 * Whether callers should skip showing an error toast for this failure.
 * True for auth/session expiry — AuthGate redirect owns that UX.
 */
export const shouldSkipErrorToast = (error: unknown): boolean => shouldSuppressAuthToast(error);
